import React from 'react'
import { useTranslation } from 'react-i18next'
import { Breadcrumbs, Crumb } from '@carsys/ui'
import { UUIDPathRegex } from '@cbgms/base/utils/uuid'

export const HistoryBreadCrumbs: React.FC<React.ComponentProps<typeof Breadcrumbs>> = ({ ...props }) => {
  const { t } = useTranslation()
  return (
    <Breadcrumbs {...props}>
      <Crumb
        data-testid='crumb-administration'
        key='crumb-administration'
        path='/purchase-invoices'
        text={t('global.sidebar_menu_administration')}
      />
      <Crumb data-testid='crumb-history-email' key='crumb-history-email' path='/history/email' text={t('Email.Title')} />
      <Crumb
        data-testid='crumb-history-email-view'
        key='crumb-history-email-view'
        path='/history/email/:uuid'
        text={t('Email.DetailPageTitle')}
      />
      <Crumb data-testid='crumb-history-sms' key='crumb-history-sms' path='/history/sms' text={t('sms.title')} />
      <Crumb
        data-testid='crumb-history-sms-view'
        key='crumb-history-sms-view'
        path='/history/sms/:uuid'
        text={t('sms.detail_page_title')}
      />
      <Crumb data-testid='crumb-history-order' key='crumb-history-order' path='/history/orders' text={t('order.orders')} />
      <Crumb
        data-testid='crumb-history-order-view'
        key='crumb-history-order-view'
        path={`/history/orders/:uuid(${UUIDPathRegex})`}
        text={t('workorder.view_order')}
      />
      <Crumb
        data-testid='crumb-history-order-create-invoice'
        key='crumb-history-order-create-invoice'
        path='/history/orders/create-invoice'
        text={t('workorder.create_invoice')}
      />
      <Crumb
        data-testid='crumb-history-packing-slips'
        key='crumb-history-packing-slips'
        path='/history/packing-slips'
        text={t('order.orders')}
      />
      <Crumb data-testid='crumb-history-invoices' key='crumb-history-invoices' path='/history/invoices' text={t('invoice.title')} />
      <Crumb
        data-testid='crumb-history-invoices-view'
        key='crumb-history-invoices-view'
        path='/history/invoices/:uuid'
        text={t('invoice.invoice')}
      />
      <Crumb data-testid='crumb-history-okr' key='crumb-history-okr' path='/history/okr' text={t('OKR.ListPageTitle')} />
      <Crumb
        data-testid='crumb-purchase-invoices'
        key='crumb-purchase-invoices'
        path='/purchase-invoices'
        exact
        text={t('purchaseInvoice.title')}
      />
      {/* Breadcrumb for packing slip detail page - shows parent invoice */}
      <Crumb
        data-testid='crumb-packing-slip-invoice'
        key='crumb-packing-slip-invoice'
        path='/purchase-invoices/:invoiceNumber/packing-slip/:packingSlipNumber'
        exact
        text={params => `${t('purchaseInvoice.title')} (${params.invoiceNumber})`}
      />
      {/* Breadcrumb for packing slip detail page - shows current packing slip */}
      <Crumb
        data-testid='crumb-packing-slip-detail'
        key='crumb-packing-slip-detail'
        path='/purchase-invoices/:invoiceNumber/packing-slip/:packingSlipNumber'
        exact
        text={params => `${t('packingSlip.title')} (${params.packingSlipNumber})`}
      />
      {/* Breadcrumb for packing slip PDF view - shows parent invoice */}
      <Crumb
        data-testid='crumb-packing-slip-view-invoice'
        key='crumb-packing-slip-view-invoice'
        path='/purchase-invoices/:invoiceNumber/packing-slip/:packingSlipNumber/view'
        text={params => `${t('purchaseInvoice.title')} (${params.invoiceNumber})`}
      />
      {/* Breadcrumb for packing slip PDF view - shows parent packing slip */}
      <Crumb
        data-testid='crumb-packing-slip-view-parent'
        key='crumb-packing-slip-view-parent'
        path='/purchase-invoices/:invoiceNumber/packing-slip/:packingSlipNumber/view'
        text={params => `${t('packingSlip.title')} (${params.packingSlipNumber})`}
      />
      {/* Breadcrumb for packing slip PDF view - shows current view */}
      <Crumb
        data-testid='crumb-packing-slip-view'
        key='crumb-packing-slip-view'
        path='/purchase-invoices/:invoiceNumber/packing-slip/:packingSlipNumber/view'
        text={params => `${t('global.view')} ${t('packingSlip.title').toLowerCase()} (${params.packingSlipNumber})`}
      />
      {/* Breadcrumb for invoice PDF view - shows parent invoice title */}
      <Crumb
        data-testid='crumb-invoice-pdf-parent'
        key='crumb-invoice-pdf-parent'
        path='/purchase-invoices/:invoiceNumber/packing-slip/:packingSlipNumber/invoice'
        text={t('purchaseInvoice.title')}
      />
      {/* Breadcrumb for invoice PDF view - shows current invoice with number */}
      <Crumb
        data-testid='crumb-invoice-pdf-view'
        key='crumb-invoice-pdf-view'
        path='/purchase-invoices/:invoiceNumber/packing-slip/:packingSlipNumber/invoice'
        text={params => `${t('purchaseInvoice.title')} (${params.invoiceNumber})`}
      />
      <Crumb data-testid='crumb-archive' key='crumb-archive' path='/archive' text={t('archive.title')} />
      <Crumb data-testid='crumb-archive-view' key='crumb-archive-view' path='/archive/:uuid' text={t('archive.order')} />
      <Crumb
        data-testid='crumb-archive-wholesaler-view'
        key='crumb-archive-wholesaler-view'
        path='/archive/:uuid/wholesalers'
        text={t('archive.wholesaler_orderdetails')}
      />
      <Crumb data-testid='crumb-archive-print-view' key='crumb-archive-print-view' path='/archive/:uuid/print' text={t('global.print')} />
      <Crumb
        data-testid='crumb-archive-checklist-print-view'
        key='crumb-archive-checklist-print-view'
        path='/archive/:uuid/checklist/:uuid/print'
        text={t('workorder.checklist_print')}
      />
    </Breadcrumbs>
  )
}

export default HistoryBreadCrumbs
